import { CONTACT_EMAIL } from '../../common/constants';
import { appLink } from '../../common/appLinks';
import { startSignup } from '../../common/auth';
import { earlyAccessPath } from '../../common/earlyAccess';
import { monthlyPrice, annualPerMonth, annualTotal } from '../../common/pricing';

// Every CTA is a live destination: Pro's checkout (useProCheckout.js,
// OpenConceptLab/ocl_online#325), the early-access form for Team, which OCL
// sets up by hand for now (ocl_online#170, #369), a mailto, or the app's own
// signup.
const mailto = subject => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;

export const DEPLOYMENT_CTA = mailto('Running OCL in production');
export const SERVICES_CTA = mailto('OCL professional services');
export const openAppCta = () => appLink('termBrowser');

// Card order, left to right. `highlighted` gives Team the badge treatment.
export const PLAN_KEYS = ['community', 'pro', 'team', 'enterprise'];
export const HIGHLIGHTED_PLAN = 'team';

// "Compare plans", under the cards (ocl_online#299). Availability only, so the
// page doesn't lead with numbers: 'yes' is included, 'preview' the free
// preview, null not included. The numbers come after it, from LIMIT_ROWS
// in common/pricing.js.
// Support is described, not marked: its cells come from pricing.compare.support.
// 'advanced' is worded, from pricing.compare.advanced.<row> (Enterprise's FHIR:
// Jon, Oct 29).
export const COMPARISON_ROWS = [
  { key: 'commons', marks: { community: 'yes', pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'exports', marks: { community: 'yes', pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'rest_api', marks: { community: 'yes', pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'fhir', marks: { community: 'preview', pro: 'yes', team: 'yes', enterprise: 'advanced' } },
  { key: 'cli', marks: { community: 'yes', pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'mcp', marks: { community: null, pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'mapper', marks: { community: 'preview', pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'matching', marks: { community: 'preview', pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'ai', marks: { community: 'preview', pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'private', marks: { community: null, pro: 'yes', team: 'yes', enterprise: 'yes' } },
  { key: 'members', marks: { community: null, pro: null, team: 'yes', enterprise: 'yes' } },
  { key: 'support', described: true },
];

// Community is free and Enterprise has no published price, so neither
// changes with the billing period — only pro/team read from PRICES.
const PRICED = ['pro', 'team'];

export const planPrice = (key, period, t) => {
  if (key === 'community')
    return { main: t('pricing.plans.community.price'), sub: null };
  if (!PRICED.includes(key))
    return { main: t('pricing.plans.enterprise.price'), sub: t('pricing.plans.enterprise.priceSub') };
  const flat = key === 'team' ? t('pricing.perMonthFlat') : t('pricing.perMonth');
  return period === 'annual'
    ? { main: `${annualPerMonth(key)}${flat}`, sub: t('pricing.billedAnnually', { total: annualTotal(key) }) }
    : { main: `${monthlyPrice(key)}${flat}`, sub: t('pricing.billedAnnuallyIfYearly', { total: annualTotal(key) }) };
};

export const planCta = (key, t, { signedIn = false, pro } = {}) => {
  // Signed out it starts the real sign-up; signed in it opens OCL Online,
  // because signed-in visitors never see "sign up" (ocl_online#170 L5).
  if (key === 'community')
    return signedIn
      ? { label: t('pricing.plans.community.cta_signed_in'), href: openAppCta() }
      : { label: t('pricing.plans.community.cta'), href: '#', onClick: startSignup };
  if (key === 'enterprise')
    return { label: t('pricing.plans.enterprise.cta'), href: DEPLOYMENT_CTA };
  // `pro` is useProCheckout's answer. Under the button: the terms while it
  // leads to checkout, and why checkout didn't open, if it didn't.
  if (key === 'pro')
    return { label: pro.label || t('pricing.plans.pro.cta'), href: pro.href, onClick: pro.onClick, notes: [pro.toCheckout && t('pricing.plans.pro.terms'), pro.error].filter(Boolean) };
  return { label: t(`pricing.plans.${key}.cta`), to: earlyAccessPath(`pricing-${key}`) };
};
